/* ============================================================
   storage — accounts (admins only).

   Who can sign in, how much they are storing, and the audit
   trail. Two guards live in the backend and are surfaced here
   rather than discovered: the last admin cannot be demoted, and
   nobody can disable or delete themselves.
   ============================================================ */

import React, { useCallback, useEffect, useState } from 'react';
import { Icon, fmtBytes, fmtDate } from './lib';
import { api } from './api';
import { ConfirmModal, NameModal } from './pickers';
import Permissions from './Permissions';

export default function AdminUsers({ token, me, toast }) {
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [dialog, setDialog] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', role: 'user' });

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [u, a] = await Promise.all([api.users(token), api.audit(token, 40)]);
      setUsers(u); setAudit(a);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    try { await fn(); toast?.('ok', okMsg); load(); }
    catch (e) { toast?.('err', 'Could not do that', e.message); }
  };

  return (
    <div className="st-admin">
      <div className="st-dash-head">
        <h2 className="h2">Accounts</h2>
        <button type="button" className="btn btn--sm" onClick={() => setAdding(true)}>
          <Icon name="plus" size={14}/> Add account
        </button>
      </div>

      {err && <div className="alert alert--err st-alert">{err}</div>}

      <div className="panel">
        {loading ? <div className="skeleton st-skel"/> : (
          <ul className="st-users">
            {users.map((u) => (
              <li key={u.id} className={`listrow st-user ${u.disabled ? 'is-off' : ''}`}>
                <Icon name="user" size={16} className="st-user-ico"/>
                <span className="st-user-name st-user-cell-name">
                  {u.username}{u.id === me.id && <span className="meta"> (you)</span>}
                </span>
                <span className={`badge st-user-role ${u.role === 'admin' ? 'st-badge-admin' : ''}`}>{u.role}</span>
                {u.disabled
                  ? <span className="badge st-badge-off st-user-flag">disabled</span>
                  : <span className="st-user-flag"/>}
                <span className="tnum st-user-size">{fmtBytes(u.bytes_used)}</span>
                <span className="meta st-user-files">{u.file_count} files</span>
                <span className="st-actions">
                  <button type="button" className="iconbtn" title="Set a new password"
                          onClick={() => setDialog({
                            type: 'password', user: u,
                          })}><Icon name="edit" size={15}/></button>
                  <button type="button" className="iconbtn" title={u.role === 'admin' ? 'Make a regular user' : 'Make an admin'}
                          onClick={() => act(() => api.updateUser(token, u.id, { role: u.role === 'admin' ? 'user' : 'admin' }),
                            `${u.username} is now ${u.role === 'admin' ? 'a user' : 'an admin'}`)}>
                    <Icon name="shield" size={15}/>
                  </button>
                  <button type="button" className="iconbtn" title={u.disabled ? 'Let them sign in again' : 'Block sign-in'}
                          disabled={u.id === me.id}
                          onClick={() => act(() => api.updateUser(token, u.id, { disabled: u.disabled ? 0 : 1 }),
                            u.disabled ? `${u.username} can sign in again` : `${u.username} is blocked`)}>
                    <Icon name={u.disabled ? 'unlock' : 'lock'} size={15}/>
                  </button>
                  <button type="button" className="iconbtn iconbtn--danger" title="Delete account and its files"
                          disabled={u.id === me.id}
                          onClick={() => setDialog({ type: 'delete', user: u })}>
                    <Icon name="trash" size={15}/>
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Permissions token={token} toast={toast}/>

      <div className="panel st-audit">
        <div className="panel-head"><span className="label">Recent activity</span></div>
        <ul className="st-audit-list">
          {audit.map((a) => (
            <li key={a.id} className="logline st-audit-row">
              <time className="meta">{fmtDate(a.ts)}</time>
              <span className="st-audit-who">{a.username || '—'}</span>
              <span className="st-audit-what">{a.action.replace(/_/g, ' ')}</span>
              <span className="meta st-audit-target">{a.meta ? (JSON.parse(a.meta).name || JSON.parse(a.meta).path || '') : ''}</span>
            </li>
          ))}
          {audit.length === 0 && <li className="empty st-empty-sm">Nothing yet.</li>}
        </ul>
      </div>

      {adding && (
        <div className="modal-backdrop st-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setAdding(false); }}>
          <div className="modal st-modal" role="dialog" aria-modal="true" aria-label="Add account">
            <div className="modal-head"><span className="label">Add account</span>
              <button type="button" className="iconbtn" onClick={() => setAdding(false)} aria-label="Close"><Icon name="close" size={14}/></button>
            </div>
            <div className="modal-body">
              <div className="field st-field">
                <label htmlFor="st-new-user">Username</label>
                <input id="st-new-user" className="input" value={form.username}
                       onChange={(e) => setForm({ ...form, username: e.target.value })}/>
                <span className="help">Lowercase letters, digits, dot, dash or underscore. At least 3.</span>
              </div>
              <div className="field st-field">
                <label htmlFor="st-new-pass">Password</label>
                <input id="st-new-pass" className="input" type="text" value={form.password}
                       onChange={(e) => setForm({ ...form, password: e.target.value })}/>
                <span className="help">At least 10 characters.</span>
              </div>
              <div className="field st-field">
                <label htmlFor="st-new-role">Role</label>
                <select id="st-new-role" className="select" value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <option value="user">user — can read everything, edits only their own</option>
                  <option value="admin">admin — can open secure folders and manage accounts</option>
                </select>
              </div>
            </div>
            <div className="modal-foot">
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setAdding(false)}>Cancel</button>
              <button type="button" className="btn btn--sm" onClick={async () => {
                try {
                  await api.createUser(token, form.username.trim().toLowerCase(), form.password, form.role);
                  toast?.('ok', 'Account created', form.username);
                  setAdding(false); setForm({ username: '', password: '', role: 'user' }); load();
                } catch (e) { toast?.('err', 'Could not create it', e.message); }
              }}>Create</button>
            </div>
          </div>
        </div>
      )}

      {dialog?.type === 'password' && (
        <NameModal title={`New password for ${dialog.user.username}`} label="Password" confirmLabel="Set password"
                   hint="At least 10 characters. They are not told — pass it on yourself."
                   onSubmit={async (pw) => { await api.updateUser(token, dialog.user.id, { password: pw }); toast?.('ok', 'Password changed'); }}
                   onClose={() => setDialog(null)}/>
      )}

      {dialog?.type === 'delete' && (
        <ConfirmModal title={`Delete ${dialog.user.username}`}
                      body={<p>The account and every file it owns ({fmtBytes(dialog.user.bytes_used)}) will be deleted from every drive, trash included. This cannot be undone.</p>}
                      confirmLabel="Delete account"
                      onConfirm={async () => { await api.deleteUser(token, dialog.user.id); toast?.('ok', 'Account deleted'); load(); }}
                      onClose={() => setDialog(null)}/>
      )}
    </div>
  );
}
